import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import SparkLine from '@prisjakt/ui-sparkline';
import Rating from '@prisjakt/ui-rating';
import Currency from '@prisjakt/ui-currency';
import { ProductConditionSummary, ProductPageQuery } from '../../../../types';
import { AppState } from '../../../../reducers';
import PriceRange from '../price-range';
import { SortOption } from '../sort-options/types';
import { getConditions } from '../product-conditions/utils';
import { IProductCondition } from '../product-conditions/types';
import * as S from './styles';

/* Scroll user to top of page and if neccessary, perform sidescroll
    in tab. Will not work on initial render but on all clicks
    Silent error on fail (not all browsers supports smooth scroll)
*/
const handleScroll = (e: React.MouseEvent<HTMLButtonElement>): void => {
  try {
    window.scrollTo(0, 0);

    if (!e.currentTarget.parentElement) {
      return;
    }

    const tabList = e.currentTarget.parentElement;
    const { offsetWidth, offsetLeft } = e.currentTarget;

    const tabNotVisible =
      offsetWidth + offsetLeft > offsetWidth || tabList.scrollLeft > offsetLeft;

    if (tabNotVisible) {
      tabList.scrollTo({
        top: 0,
        left: offsetLeft,
        behavior: 'smooth',
      });
    }
  } catch (err) {} // eslint-disable-line no-empty
};

const getPriceRange = (
  conditions: ProductConditionSummary[],
  selectedCondition: string,
  currentSort: SortOption
): IProductCondition | undefined => {
  const conditionsWithCalcShipping = getConditions(conditions, currentSort);
  return conditionsWithCalcShipping.find(c => c.name === selectedCondition);
};

type TabListProps = {
  isSticky: boolean;
  product: ProductPageQuery['product'];
  routeSection: 'main' | 'properties' | 'reviews' | 'statistics';
  productConditionVisible: boolean;
  selectedCondition: string | null;
};

const TabList = ({
  isSticky,
  routeSection,
  product,
  productConditionVisible,
  selectedCondition,
}: TabListProps) => {
  const { t, linkTo } = useContext(AppContext);
  const currentSort = useSelector(
    (state: AppState) => state.areas.product.sort
  );

  if (!product) {
    return null;
  }

  const priceRange =
    productConditionVisible &&
    product?.conditions?.length &&
    selectedCondition &&
    getPriceRange(product.conditions, selectedCondition, currentSort);

  return (
    <S.TabList isSticky={isSticky}>
      <S.Tab
        active={routeSection === 'main'}
        to={linkTo('product', product)}
        // @ts-ignore
        onClick={handleScroll}
        data-test="TabButton"
        data-test-section="main"
        aria-label={t('product', 'tabs.prices')}
        minWidth={90}
      >
        <S.TabTitle>{t('product', 'tabs.prices')}</S.TabTitle>
        {priceRange ? (
          <PriceRange priceFrom={priceRange.from} priceTo={priceRange.to} />
        ) : (
          <S.TabLowestPrice>
            {product.priceSummary && (
              <Currency
                price={
                  product.priceSummary.regular ||
                  product.priceSummary.alternative
                }
              />
            )}
          </S.TabLowestPrice>
        )}
      </S.Tab>

      <S.Tab
        active={routeSection === 'properties'}
        to={linkTo('productProperties', product)}
        // @ts-ignore
        onClick={handleScroll}
        data-test="TabButton"
        data-test-section="properties"
        aria-label={t('product', 'product.information')}
        minWidth={100}
      >
        <S.TabTitle>{t('product', 'tabs.properties')}</S.TabTitle>
        <S.TabSubtitle>
          {product.coreProperties && product.coreProperties.nodes && (
            <S.StyledCoreProperties
              toneDown
              size="tiny"
              maxrows={2}
              properties={product.coreProperties.nodes
                .map(node => node.pretty || '')
                .filter(Boolean)}
            />
          )}
        </S.TabSubtitle>
      </S.Tab>

      <S.Tab
        active={routeSection === 'reviews'}
        to={linkTo('productReviews', product)}
        // @ts-ignore
        onClick={handleScroll}
        data-test="TabButton"
        data-test-section="reviews"
        aria-label={t('product', 'tabs.reviews')}
        minWidth={145}
      >
        <S.TabTitle>{t('product', 'tabs.reviews')}</S.TabTitle>
        <S.TabRating>
          {product.userReviewSummary &&
          product.userReviewSummary.count &&
          product.userReviewSummary.count > 0 ? (
            <Rating
              rate={product.userReviewSummary.rating}
              count={product.userReviewSummary.count}
              size="small"
              showRate
              showCounterAs="number"
              showRateAs="number"
            />
          ) : null}
        </S.TabRating>
      </S.Tab>

      <S.Tab
        active={routeSection === 'statistics'}
        to={linkTo('productPriceHistory', product)}
        // @ts-ignore
        onClick={handleScroll}
        data-test="TabButton"
        data-test-section="statistics"
        aria-label={t('product', 'tabs.price-history')}
        minWidth={100}
      >
        <S.TabTitle>{t('product', 'tabs.price-history')}</S.TabTitle>
        <S.TabSparkline>
          {product.sparkline && product.sparkline.values && (
            <SparkLine
              width={40}
              height={10}
              values={product.sparkline.values}
              disableAnimation
            />
          )}
        </S.TabSparkline>
      </S.Tab>
    </S.TabList>
  );
};

export default TabList;
